import {
  createActivity,
  updateActivity,
  listActivityAttachments,
  uploadActivityAttachments,
  deleteActivityAttachment,
  getActivitiesInRange,
} from '@/api/activity.api'
import { EMPTY_ACTIVITY } from '@/common/activity.common'
import { submitActivityDrafts } from '@/common/activityDrafts'
import { getErrorMessage } from '@/helper/apiError.helpers'
import {
  ATTACHMENT_ACCEPT_ATTR,
  MAX_ATTACHMENT_FILES,
  MAX_ATTACHMENT_FILE_SIZE,
  describeRejectedAttachments,
  translateAttachmentError,
  validateAttachments,
} from '@/common/attachments'
import { Tooltip } from '@/components/ui/tooltip'
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Combobox,
  createListCollection,
  Field,
  Flex,
  IconButton,
  Input,
  Checkbox,
  Portal,
  SegmentGroup,
  Select,
  Stack,
  Stat,
  Status,
  Text,
  Textarea,
  useMediaQuery,
  chakra,
} from '@chakra-ui/react'
import { useFormik, type FormikHelpers } from 'formik'
import { useMemo, useRef, useState, useEffect, useCallback } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { LuUpload, LuX, LuInfo, LuTimerOff } from 'react-icons/lu'
import * as Yup from 'yup'
import { toast } from '../ui/toaster/toasterHelpers'
import type { BillingVariant, CreateActivityDTO } from '@/api/dto/activity.dto'
import type { ContactBookDto } from '@/api/dto/settings.dto'
import dayjs from 'dayjs'
import { API_BASE_URL, SUPPORT_PORTAL_URL } from '@/config'
import {
  getAllBanks,
  getBanks,
  getBankServices,
  getContactBooks,
  getServices,
} from '@/api/settings.api'
import {
  resolveApprovedOrderId,
  normalizeSvc,
  normalizeServiceForBilling,
  getAdvisorNumberState,
  isTojService,
  timeToMinutes,
  digitsOnly,
  padAdvisorNumber,
  getQtyConstraints,
  clampAndSnapQty,
  computeBillingVariant,
  getQtyHint,
  getAutoDurationMinutes,
  roundUpToNextQuarterHour,
  formatTimeHHmm,
  addMinutesToHHmm,
  isInvoicedActivityConflict,
  getInvoicedActivityConflictMessage,
  toVisibleAdvisorNumber,
  // (optional) formatActivityDate/uiQtyFromForm if you added them
} from '@/helper/activity.helper'
import TojPlausibilityDialog from '../modals/TojPlausibilityDialog'
import { getWorkload } from '@/api/orders.api'
import { hoursToUnits } from '@/helper/order.helper'
import { useUserStore } from '@/stores/user.store'
import Mini3DayCalendar from './Mini3DayCalendar'
import { SibPageShell } from '../layout/SibPageShell'
import { Page } from '../layout/Page'
import { FormLayout } from '../layout/FormLayout'
import { DualActionBar } from '../common/DualActionBar'
import { WorkloadQuotaCard, WorkloadQuotaHint } from '../common/WorkloadQuotaCard'
import { OptionalFieldBadge } from '@/components/form/OptionalFieldBadge'
import { SettingsInput } from '@/pages/settings/SettingsControls'
import { getUserDisplayName } from '@/helper/common.helper'
import { endUserSession } from '@/providers/auth/session'

type ServiceRow = {
  id: string
  serviceCode: string
  quantity: number | ''
}

type ActivityFormValues = Omit<CreateActivityDTO, 'date' | 'location'> & {
  date: Date
  services: ServiceRow[]
  contact?: string
  location: {
    type: 'online' | 'onsite'
    city: string
  }
  billingVariant?: BillingVariant
  orderId?: number
  activityStartTime?: string
  activityEndTime?: string
  advisorNumber?: string
  contactBookId?: number
}

function formatAdvisorLabel(advisor: ContactBookDto) {
  const fullName = [advisor.firstName, advisor.lastName].filter(Boolean).join(' ').trim()
  return fullName ? `${fullName} (${advisor.advisorNumber})` : advisor.advisorNumber
}

type AdvisorComboboxItem = {
  label: string
  value: string
  advisor?: ContactBookDto
  manualAdvisorNumber?: string
}

/**
 * @component CreateActivityPopup
 * @description
 * A modal dialog used to create new activities or edit existing ones, with support for
 * managing multiple activity drafts simultaneously.
 *
 * It allows the user to:
 * - Create **multiple activity drafts** in a single session, switching between them via tabs.
 * - Enter a **title**, select **tags** (optional), and pick a **date** for the activity.
 * - Choose **Auftraggeber** (Union Investment - the customer) and **Leistungsempfänger** (bank - service provider) from dropdown lists.
 * - Select an **activity type** (ToJ, ToJ mit Kunde, Beratung, Schulung) and specify **quantity**.
 * - Define the **location** (Vor Ort/Online) with a city field that's disabled when "Online" is selected.
 * - Add optional **notes** (up to 3000 characters) with a live character counter.
 * - **Edit existing activities** when an `editId` is provided (draft tabs hidden in edit mode).
 *
 * Includes:
 * - Full **Yup validation** via Formik for all required fields and conditional validation.
 * - Real-time **draft validation** with visual indicators showing which drafts are valid/invalid.
 * - Automatic **field synchronization** between Formik state and draft storage.
 * - Submit button disabled until all drafts pass validation.
 * - Success toast notification upon successful creation/update.
 * - Error handling with user-friendly status messages.
 *
 * @prop {Partial<CreateActivityDTO>} [values] - Initial values to populate the form (used in edit mode).
 * @prop {Item[]} customers - List of available customers (Auftraggeber - Union Investment) with `label` and `value` (number).
 * @prop {Item[]} banks - List of available banks (Leistungsempfänger - service providers) with `label` and `value` (number).
 * @prop {Item[]} tags - List of available tags with `label` and `value` (string).
 * @prop {boolean} [visible] - Controls whether the dialog is open.
 * @prop {() => void} [onClose] - Callback fired when the dialog should close.
 * @prop {string} [editId] - If provided, the form operates in edit mode for the specified activity ID.
 *
 *
 * @example
 * ```tsx
 * <CreateActivityPopup
 *   visible={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   customers={[
 *     { label: "Union Investment", value: 1 }
 *   ]}
 *   banks={[
 *     { label: "Finovia Bank AG", value: 1 },
 *     { label: "Alpha Retail GmbH", value: 2 }
 *   ]}
 *   tags={[
 *     { label: "Dringend", value: "urgent" },
 *     { label: "Follow-up", value: "followup" }
 *   ]}
 * />
 * ```
 */

const CITY_NAME_REGEX = /^[A-Za-zÀ-ÖØ-öø-ÿĀ-žА-Яа-яÄÖÜäöüß\s.'-]+$/
const createActivitySchema = Yup.object({
  date: Yup.date().required('Ungültiges Datum'),
  // .test(
  //   'not-in-future',
  //   'Bitte wählen Sie ein Datum in der Vergangenheit oder heute.',
  //   value => {
  //     if (!value) return false
  //     return !dayjs(value).isAfter(dayjs(), 'day')
  //   }
  // ),

  bankId: Yup.number().min(1, 'Bank ist erforderlich').required('Bank ist erforderlich'),
  customerId: Yup.number()
    .min(1, 'Leistungsempfänger ist erforderlich')
    .required('Leistungsempfänger ist erforderlich'),
  type: Yup.string().required('Leistung ist erforderlich'),

  timeQuantity: Yup.number()
    .required('Menge ist erforderlich')
    .test('qty-by-service', 'Ungültige Menge', function (val) {
      const v = Number(val)
      if (!Number.isFinite(v)) return false

      const typeRaw = (this.parent as any)?.type as string | undefined
      const svc = normalizeServiceForBilling(typeRaw)

      if (svc === 'TOJ') {
        if (!Number.isInteger(v))
          return this.createError({ message: 'ToJ: nur ganze Zahlen erlaubt.' })
        if (v < 1) return this.createError({ message: 'ToJ: mindestens 1.' })
        return true
      }

      if (svc === 'PILOT') {
        if (!Number.isInteger(v))
          return this.createError({ message: 'Pilot: nur ganze Zahlen erlaubt.' })
        if (v < 1) return this.createError({ message: 'Pilot: mindestens 1.' })
        return true
      }

      if (svc === 'CONSULTING') {
        if (v < 0.5 || v > 10) {
          return this.createError({ message: 'Beratung: erlaubt 0,5 bis 10.' })
        }
        if (Math.round(v * 2) !== v * 2) {
          return this.createError({ message: 'Beratung: nur 0,5er Schritte.' })
        }
        return true
      }

      if (svc === 'TRAINING') {
        if (v !== 0.5 && v !== 1) {
          return this.createError({ message: 'Schulung: nur 0,5 oder 1 erlaubt.' })
        }
        return true
      }

      // fallback
      return v >= 1
    }),

  location: Yup.object()
    .shape({
      type: Yup.string()
        .oneOf(['onsite', 'online'], 'Ort ist erforderlich')
        .required('Ort ist erforderlich'),
      city: Yup.string().when('type', {
        is: 'onsite',
        then: s =>
          s
            .trim()
            .required('Stadt ist erforderlich')
            .matches(CITY_NAME_REGEX, 'Bitte geben Sie einen gültigen Ortsnamen ein.')
            .min(2, 'Ort muss mindestens 2 Zeichen haben.')
            .max(100, 'Ort darf maximal 100 Zeichen haben.'),
        otherwise: s => s.notRequired(),
      }),
    })
    .required('Ort ist erforderlich'),
  notes: Yup.string().max(1000, 'Notiz darf maximal 1000 Zeichen haben.').optional(),
  activityStartTime: Yup.string().when('$isUnionSelected', {
    is: true,
    then: s => s.required('Uhrzeit von ist erforderlich'),
    otherwise: s => s.notRequired(),
  }),
  activityEndTime: Yup.string()
    .when('$isUnionSelected', {
      is: true,
      then: s => s.required('Bis ist erforderlich'),
      otherwise: s => s.notRequired(),
    })
    .test('end-after-start', 'Bis muss nach Von liegen.', function (end) {
      const start = (this.parent as any)?.activityStartTime as string | undefined
      if (!start || !end) return true // only check if both exist
      const sm = timeToMinutes(start)
      const em = timeToMinutes(end)
      if (sm == null || em == null) return true // let format be handled elsewhere
      return em > sm
    }),
  // Editable for TOJ and consulting hours; mandatory only for TOJ (SIB-926).
  advisorNumber: Yup.string().when('$isAdvisorEditable', {
    is: true,
    then: s =>
      s
        .when('$isAdvisorRequired', {
          is: true,
          then: r => r.required('Berater auswählen oder Nummer eintragen'),
          otherwise: r => r.notRequired(),
        })
        .test(
          'advisor-1-to-8-digits',
          'Beraternummer muss 1 bis 8-stellig sein (nur Ziffern).',
          v => {
            if (!v) return true // an empty value is handled by required() above
            const d = digitsOnly(v)
            return d.length >= 1 && d.length <= 8
          }
        ),
    otherwise: s => s.notRequired(),
  }),
})

export type Item = {
  label: string
  value: number | string
  // Not yet returned by the API for associated entities; included for future bank search support
  postal?: string | null
  blzNum?: string | null
  accountNumber?: string | null
}

type AttachmentItem = {
  id: string
  file: File
}

const UNION_SERVICE_OPTIONS = [
  { label: 'ToJ', value: 'TOJ' },
  { label: 'Beratung', value: 'CONSULTING' },
  { label: 'Schulung', value: 'TRAINING' },
  { label: 'ToJ mit Kunde', value: 'TOJ_CUSTOMER' },
]

const BILLING_VARIANTS_BY_TYPE: Record<string, { label: string; value: BillingVariant }[]> = {
  TOJ: [
    { label: 'Standard (Normalpreis)', value: 'STANDARD' },
    { label: '80% (ToJ)', value: 'EIGHTY_PERCENT_TOJ' },
  ],
  TRAINING: [
    { label: 'Standard (Normalpreis)', value: 'STANDARD' },
    { label: '80% (Schulung)', value: 'EIGHTY_PERCENT_TRAINING' },
    { label: 'Halber Schulungstag (fix 4h, Normalpreis)', value: 'HALF_TRAINING_DAY' },
  ],
  CONSULTING: [
    { label: 'Standard (Normalpreis)', value: 'STANDARD' },
    { label: '80% (Beratung)', value: 'EIGHTY_PERCENT_CONSULTING' },
    { label: 'Halbe Einheit (fix 0,5h, Normalpreis)', value: 'HALF_HOUR_CONSULTING' },
  ],
}

export interface CreateActivityPageProps {
  values?: Partial<CreateActivityDTO>
  customers?: Item[]
  banks?: Item[]
  tags?: Item[]
  services?: { label: string; value: string }[]
  acceptedOrders?: any[]
}

function isEightyPercentVariant(variant?: BillingVariant | null) {
  return (
    variant === 'EIGHTY_PERCENT_TOJ' ||
    variant === 'EIGHTY_PERCENT_TRAINING' ||
    variant === 'EIGHTY_PERCENT_CONSULTING'
  )
}

function makeInitialValues(
  values?: Partial<CreateActivityDTO>,
  swapIdsForForm = false
): ActivityFormValues {
  const baseDTO: CreateActivityDTO = { ...EMPTY_ACTIVITY, ...(values ?? {}) }

  const normalizedDTO = swapIdsForForm
    ? { ...baseDTO, bankId: baseDTO.customerId, customerId: baseDTO.bankId }
    : baseDTO

  const initialDate =
    normalizedDTO.date instanceof Date
      ? normalizedDTO.date
      : new Date(normalizedDTO.date ?? new Date())

  const formLocationType: 'online' | 'onsite' =
    normalizedDTO.location?.type === 'ONSITE' ? 'onsite' : 'online'

  const services: ServiceRow[] = [
    {
      id: 'service-0',
      serviceCode: normalizedDTO.type ?? '',
      quantity: normalizedDTO.timeQuantity ?? 0,
    },
  ]

  const billingVariant = (normalizedDTO as any).billingVariant ?? 'STANDARD'

  return {
    ...normalizedDTO,
    date: initialDate,
    services,
    contact: normalizedDTO.contact ?? '',
    billingVariant,
    shortNoticeCancelled:
      typeof (normalizedDTO as any).shortNoticeCancelled === 'boolean'
        ? Boolean((normalizedDTO as any).shortNoticeCancelled)
        : isEightyPercentVariant(billingVariant),
    advisorNumber: normalizedDTO.advisorNumber
      ? toVisibleAdvisorNumber(normalizedDTO.advisorNumber)
      : '',
    contactBookId: normalizedDTO.contactBookId,
    location: {
      type: formLocationType,
      city: normalizedDTO.location?.city ?? '',
    },
  }
}

function formatFileSize(bytes?: number) {
  if (!bytes || bytes <= 0) return '0 KB'
  if (bytes < 1024) return '1 KB'
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export default function CreateActivityPage(props: CreateActivityPageProps) {
  const nav = useNavigate()
  // ✅ paths / urls (adjust to your app)
  const dashboardPath = '/app/user/dashboard'
  const navigate = useNavigate()
  // ✅ header actions (simple versions)
  const handleNewOrder = useCallback(() => {
    navigate(dashboardPath, { state: { action: 'new-order' } })
  }, [navigate, dashboardPath])
  const [workloadRefreshKey, setWorkloadRefreshKey] = useState(0)
  const handleAddActivity = () => nav('/activities/new') // or the route that opens this page
  const handleCreateInvoice = useCallback(() => {
    // consistent with your dashboard: currently TODO
    console.log('Rechnung erstellen')
    navigate(dashboardPath, { state: { action: 'create-invoice' } })
  }, [navigate, dashboardPath])

  const { id } = useParams()
  const location = useLocation()

  const state = (location.state ?? {}) as Partial<CreateActivityPageProps> & { values?: any }

  const [resolvedCustomers, setResolvedCustomers] = useState<Item[]>(state.customers ?? [])
  const [resolvedBanks, setResolvedBanks] = useState<Item[]>(state.banks ?? [])
  const [resolvedServices, setResolvedServices] = useState<{ label: string; value: string }[]>(
    state.services ?? []
  )
  const [resolvedAcceptedOrders, setResolvedAcceptedOrders] = useState<any[]>(
    state.acceptedOrders ?? []
  )

  const [workload, setWorkload] = useState<Record<string, number> | null>(null)
  const [workloadLoading, setWorkloadLoading] = useState(false)
  const [workloadError, setWorkloadError] = useState<string | null>(null)

  const customers = props.customers ?? resolvedCustomers
  const banks = props.banks ?? resolvedBanks
  // const services = props.services ?? resolvedServices
  const acceptedOrders = props.acceptedOrders ?? resolvedAcceptedOrders

  const editId = id ? String(id) : undefined

  const values = props.values ?? state.values

  const unionCustomer = useMemo(
    () => customers.find(c => (c.label ?? '').toLowerCase().includes('union')),
    [customers]
  )

  const lastNoOrderKeyRef = useRef<string>('')
  const tojSkipRef = useRef(false)
  const startTimeTouchedByUserRef = useRef(false)
  const endTimeTouchedByUserRef = useRef(false)

  const [isMobile] = useMediaQuery(['(max-width: 767px)'])

  const customerItems = useMemo(() => createListCollection({ items: customers }), [customers])
  // const bankItems = useMemo(() => createListCollection({ items: banks }), [banks])
  const unionId = Number(unionCustomer?.value || 1)

  // Detect if the activity coming from backend is stored in "swapped" format:
  // bankId = union, customerId = entity
  const storedIsSwapped = useMemo(() => {
    if (!editId) return false
    const b = Number((values as any)?.bankId)
    const c = Number((values as any)?.customerId)
    if (!b || !c) return false
    return b === unionId && c !== unionId
  }, [editId, values, unionId])
  const initialValues = useMemo(() => {
    const base = makeInitialValues(values, storedIsSwapped)

    // only for create mode: preselect Union as Auftraggeber
    if (!editId && unionCustomer) {
      return {
        ...base,
        customerId: Number(unionCustomer.value),
      }
    }

    return base
  }, [values, storedIsSwapped, editId, unionCustomer])
  // const [bankServicesLoading, setBankServicesLoading] = useState(false)
  const [bankServiceOptions, setBankServiceOptions] = useState<{ label: string; value: string }[]>(
    []
  )

  async function getExistingTojForDay(date: Date, excludeActivityId?: string) {
    const from = dayjs(date).startOf('day').toDate()
    const to = dayjs(date).endOf('day').toDate()

    const res = await getActivitiesInRange(from, to)
    const items = res.items ?? []

    const sum = items.reduce((acc, a: any) => {
      if (excludeActivityId && String(a.id) === String(excludeActivityId)) {
        return acc
      }

      const t = normalizeSvc(a.type)
      if (t === 'TOJ' || t === 'TOJ_CUSTOMER') {
        return acc + Number(a.timeQuantity ?? 0)
      }

      return acc
    }, 0)

    return sum
  }
  const [tojConfirmOpen, setTojConfirmOpen] = useState(false)
  const [tojConfirmed, setTojConfirmed] = useState(false)
  const pendingSubmitRef = useRef<null | (() => void)>(null)

  async function ensureTojPlausibility(values: ActivityFormValues) {
    if (!isUnionSelected) return true

    const code = values.services?.[0]?.serviceCode ?? values.type
    if (!isTojService(code)) return true

    const qty = Number(values.timeQuantity ?? values.services?.[0]?.quantity ?? 0)
    if (!qty) return true

    const existing = await getExistingTojForDay(values.date, editId)
    const total = existing + qty

    if (total <= 3) return true

    // reset skip flag before opening dialog
    tojSkipRef.current = false
    setTojConfirmOpen(true)
    return false
  }

  const [drafts, setDrafts] = useState<ActivityFormValues[]>([initialValues])
  // const [draftValidities, setDraftValidities] = useState<boolean[]>([false])

  // Stable identity per draft, parallel to `drafts`. createdActivityIdsRef keys off these
  // rather than array positions, so adding, removing or reordering drafts between submit
  // attempts can never make a retry skip a new draft or attach files to the wrong activity.
  // Any future code that changes the shape of `drafts` must change `draftKeys` in step.
  const draftKeySeqRef = useRef(0)
  const nextDraftKey = () => `draft-${draftKeySeqRef.current++}`
  const [draftKeys, setDraftKeys] = useState<string[]>(() => [nextDraftKey()])

  /**
   * Ids of activities already persisted during a previous submit attempt, keyed by draft key.
   * Prevents a retry after a partial failure from creating duplicates. A draft whose create
   * call succeeded but whose id could not be resolved is recorded as UNRESOLVED_ACTIVITY_ID,
   * which still blocks re-creation even though attachments can no longer be uploaded for it.
   */
  const createdActivityIdsRef = useRef<Record<string, number>>({})

  const [selectedDraftIndex, setSelectedDraftIndex] = useState<number>(0)
  const [attachments, setAttachments] = useState<AttachmentItem[]>([])
  const [draftAttachments, setDraftAttachments] = useState<AttachmentItem[][]>([[]])
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const handleDeleteExistingAttachment = async (attachmentId: string) => {
    if (!editId || isReadOnly) return

    try {
      await deleteActivityAttachment(Number(editId), attachmentId)

      // remove from state
      setExistingAttachments(prev => prev.filter(a => String(a.id) !== String(attachmentId)))

      toast.success('Anhang gelöscht', '', { duration: 1500 })
    } catch (error) {
      console.error('Failed to delete attachment', error)
      toast.error('Anhang konnte nicht gelöscht werden')
    }
  }

  type ExistingAttachmentItem = {
    id: string
    name: string
    size?: number
    downloadUrl?: string
  }

  const [existingAttachments, setExistingAttachments] = useState<ExistingAttachmentItem[]>([])
  const INVOICED_ACTIVITY_LOCK_MESSAGE =
    'Diese Aktivität wurde bereits in Rechnung gestellt und kann nicht mehr bearbeitet oder gelöscht werden.'

  const TOJ_CUSTOMER_OPTION = { label: 'ToJ mit Kunde', value: 'TOJ_CUSTOMER' }
  const UNION_ONLY_CODES = new Set(['TOJ', 'TOJ_CUSTOMER', 'PILOT'])

  const serviceOptions = useMemo(() => {
    const base = bankServiceOptions ?? []
    return [...base, TOJ_CUSTOMER_OPTION]
  }, [bankServiceOptions])

  useEffect(() => {
    setDrafts([initialValues])
    // setDraftValidities([false])
    setDraftKeys([nextDraftKey()])
    createdActivityIdsRef.current = {}
    setSelectedDraftIndex(0)
    setAttachments([])
    setDraftAttachments([[]])
    startTimeTouchedByUserRef.current = false
    endTimeTouchedByUserRef.current = false
  }, [initialValues, editId])

  const formik = useFormik<ActivityFormValues>({
    initialValues,
    enableReinitialize: true,
    validateOnBlur: true,
    validateOnChange: true,
    validateOnMount: true,
    validationSchema: createActivitySchema,
    validate: async values => {
      try {
        await createActivitySchema.validate(values, {
          abortEarly: false,
          context: { isUnionSelected, isAdvisorEditable, isAdvisorRequired },
        })
        return {}
      } catch (err: any) {
        if (!err?.inner) return {}
        const errors: Record<string, string> = {}
        for (const e of err.inner) {
          if (e.path && !errors[e.path]) errors[e.path] = e.message
        }
        return errors
      }
    },
    onSubmit: async (values, handlers) => {
      if (hasInsufficientCapacity) {
        handlers.setStatus({
          type: 'error',
          message: 'Für diese Leistung ist kein ausreichendes Kontingent mehr verfügbar.',
        })
        return
      }

      const ok = await ensureTojPlausibility(values)
      if (!ok) {
        pendingSubmitRef.current = () => {
          // After confirmation, actually submit
          const submitted = !editId
            ? handleSubmitNewActivities(handlers)
            : handleUpdateActivity(values, handlers)

          void submitted.catch(error => {
            console.error(error)
            handlers.setSubmitting(false)
          })
        }
        return
      }

      if (!editId) await handleSubmitNewActivities(handlers)
      else await handleUpdateActivity(values, handlers)
    },
  })

  const submitModeRef = useRef<'save' | 'saveAndNew'>('save')

  const handleCancel = () => nav(-1)

  const submitSave = () => {
    submitModeRef.current = 'save'
    formik.submitForm()
  }

  const submitSaveAndNew = () => {
    submitModeRef.current = 'saveAndNew'
    formik.submitForm()
  }

  const year = useMemo(() => dayjs(formik.values.date).year(), [formik.values.date])
  const from = useMemo(() => `${year}-01-01T00:00:00`, [year])
  const to = useMemo(() => `${year}-12-31T23:59:59`, [year])
  const selectedEntityId = useMemo(() => Number(formik.values.bankId || 0), [formik.values.bankId])
  //Clear times for non Union custoemrs
  const handleClearTimes = () => {
    startTimeTouchedByUserRef.current = true
    endTimeTouchedByUserRef.current = true

    formik.setFieldValue('activityStartTime', '', true)
    formik.setFieldValue('activityEndTime', '', true)

    handleDraftChange('activityStartTime' as any, '')
    handleDraftChange('activityEndTime' as any, '')

    formik.setFieldTouched('activityStartTime', false, false)
    formik.setFieldTouched('activityEndTime', false, false)
    formik.setStatus(null)
  }

  useEffect(() => {
    const updatedDrafts = [...drafts]
    updatedDrafts[selectedDraftIndex] = formik.values
    syncDraftsValidity(updatedDrafts)
  }, [formik.values])

  useEffect(() => {
    let cancelled = false

    async function loadFallbackData() {
      // already have data -> no fetch
      if ((state.customers?.length ?? 0) > 0 && (state.services?.length ?? 0) > 0) return

      try {
        // 1) load services
        const srv = await getServices()
        const serviceOptions = (srv ?? [])
          .filter((s: any) => s.active !== false)
          .sort(
            (a: any, b: any) =>
              (a.displayOrder ?? 9999) - (b.displayOrder ?? 9999) ||
              String(a.name).localeCompare(String(b.name))
          )
          .map((s: any) => ({ label: s.name, value: s.code }))

        // 2) load banks + union associated entities
        const response = await getBanks()
        const list = response.items ?? []
        const bankItems = list.map((b: any) => ({ label: b.name, value: b.id }))

        const union = list.find((b: any) => (b.name ?? '').toLowerCase().includes('union'))
        let entityItems: Item[] = []

        if (union) {
          // Use getAllBanks instead of getBankById's associatedEntities, which only
          // returns id/name/active and not the postal/blzNum needed for bank search
          const childBanks = await getAllBanks({ parentId: union.id })
          entityItems = childBanks.map((b: any) => ({
            label: b.name,
            value: String(b.id),
            postal: b.postal ?? null,
            blzNum: b.blzNum ?? null,
            accountNumber: b.accountNumber ?? null,
          }))
        }

        // 3) accepted orders (optional but recommended)
        // If you can access user here, fetch orders as in calendar and filter approved.
        // Otherwise keep it empty and orderId logic will just not run.

        if (!cancelled) {
          setResolvedCustomers(bankItems) // Auftraggeber dropdown uses "banks list"
          setResolvedBanks(entityItems) // Leistungsempfänger dropdown uses entities (later filtered by approved orders if you want)
          setResolvedServices(serviceOptions)
        }
      } catch (e) {
        console.error('fallback load failed', e)
      }
    }

    loadFallbackData()
    return () => {
      cancelled = true
    }
  }, [location.key]) // important: rerun every time you navigate to this page

  useEffect(() => {
    const bankId = Number(formik.values.bankId)
    let cancelled = false

    ;(async () => {
      try {
        const res = await getBankServices(bankId)

        const opts =
          (res.items ?? []).map(s => ({
            // show name, but keep code as value
            label: s.serviceName ?? s.serviceCode,
            value: s.serviceCode,
          })) ?? []

        if (!cancelled) setBankServiceOptions(opts)
      } catch (e) {
        console.error('Failed to load bank services for bankId', bankId, e)
        if (!cancelled) setBankServiceOptions([])
      }
    })()

    return () => {
      cancelled = true
    }
  }, [formik.values.bankId])

  useEffect(() => {
    if (!editId) return

    const loadAttachments = async () => {
      try {
        const data = await listActivityAttachments(Number(editId))

        const mapped: ExistingAttachmentItem[] = (data.attachments ?? []).map(item => ({
          id: String(item.id),
          name: item.filename,
          size: item.fileSize,
          downloadUrl: `${API_BASE_URL}/activities/${editId}/attachments/${item.id}/download`,
        }))

        setExistingAttachments(mapped)
      } catch (error) {
        console.error('Failed to load attachments for activity', editId, error)
      }
    }

    loadAttachments()
  }, [editId])

  const selectedCustomer = useMemo(() => {
    return customers.find(c => Number(c.value) === Number(formik.values.customerId))
  }, [customers, formik.values.customerId])
  const { user, profile } = useUserStore()
  const displayName = getUserDisplayName(user, profile)
  const isUnionCustomer = !!selectedCustomer?.label?.toLowerCase().includes('union')

  const isUnionRecipient = useMemo(() => {
    const selectedBankId = Number((formik.values as any).bankId)
    if (!selectedBankId) return false
    const bankEntry = banks.find((b: Item) => Number(b.value) === selectedBankId)
    return !!bankEntry?.label?.toLowerCase().includes('union')
  }, [banks, (formik.values as any).bankId])

  const availableServiceTypes = useMemo(() => {
    if (isUnionCustomer) {
      // Union must always see TOJ / Beratung / Schulung (+ ToJ mit Kunde already in serviceOptions)
      const merged = [...serviceOptions, ...UNION_SERVICE_OPTIONS]

      // Add Pilot only when Leistungsempfänger is also a Union entity
      if (isUnionRecipient) {
        merged.push({ label: 'Pilot', value: 'PILOT' })
      }

      // de-dupe by value
      const seen = new Set<string>()
      const items = merged.filter(opt => {
        const key = String(opt.value)
        if (seen.has(key)) return false
        seen.add(key)
        return true
      })

      return createListCollection({ items })
    }

    // non-Union: keep old behavior
    const items = serviceOptions.filter(s => !UNION_ONLY_CODES.has(String(s.value)))
    return createListCollection({ items })
  }, [isUnionCustomer, isUnionRecipient, serviceOptions])

  useEffect(() => {
    const currentCode = formik.values.services?.[0]?.serviceCode ?? formik.values.type
    if (!currentCode) return

    //  don't clear while options are still loading
    const optionsReady =
      availableServiceTypes.items.length > 0 &&
      // for non-Union we rely on getBankServices(bankId)
      (isUnionCustomer || bankServiceOptions.length > 0)

    if (!optionsReady) return

    const stillAllowed = availableServiceTypes.items.some(
      t => String(t.value) === String(currentCode)
    )
    if (!stillAllowed) {
      handleServiceTypeChange(0, '')
    }
  }, [availableServiceTypes, formik.values.services, isUnionCustomer, bankServiceOptions.length])

  const syncDraftsValidity = (updatedDrafts: ActivityFormValues[]) => {
    setDrafts(updatedDrafts)
    // setDraftValidities(updatedDrafts.map(draft =>
    //   createActivitySchema.isValidSync(draft, { context: { isUnionSelected } })
    // ))
  }

  const toDTO = (values: ActivityFormValues): CreateActivityDTO => {
    const codeRaw = values.services?.[0]?.serviceCode ?? values.type
    const needsSkip = isUnionSelected && isTojService(codeRaw) && tojSkipRef.current

    // UI quantity (units)
    const uiQty =
      values.services?.[0]?.quantity !== ''
        ? Number(values.services?.[0]?.quantity)
        : Number(values.timeQuantity ?? 0)

    const cancelled = Boolean((values as any).shortNoticeCancelled)

    const billingVariant = isUnionSelected
      ? computeBillingVariant({
          serviceCode: codeRaw,
          qty: uiQty,
          shortNoticeCancelled: cancelled,
        })
      : values.billingVariant

    return {
      type: values.type,
      customerId: values.customerId,
      bankId: values.bankId,
      date: dayjs(values.date).format('YYYY-MM-DD[T]HH:mm:ss'),

      // backend expects hours
      timeQuantity: uiQty,

      billingVariant,
      location: {
        type: values.location.type === 'online' ? 'ONLINE' : 'ONSITE',
        city: values.location.city ?? '',
      },
      notes: values.notes ?? '',
      contact: values.contact ?? '',
      ...(values.orderId && values.orderId > 0 ? { orderId: values.orderId } : {}),
      ...(isAdvisorEditable && values.contactBookId
        ? { contactBookId: values.contactBookId }
        : {}),
      skipTojQuantityCheck: needsSkip,

      activityStartTime: values.activityStartTime ?? undefined,
      activityEndTime: values.activityEndTime ?? undefined,
      advisorNumber:
        isAdvisorEditable && digitsOnly(values.advisorNumber ?? '')
          ? padAdvisorNumber(values.advisorNumber ?? '')
          : undefined,
      shortNoticeCancelled: cancelled,
    }
  }

  const handleSubmitNewActivities = async ({
    setSubmitting,
    setStatus,
  }: FormikHelpers<ActivityFormValues>) => {
    setSubmitting(true)

    // Retry semantics (skip already-created activities, drop already-uploaded files, keep
    // the failed ones) live in submitActivityDrafts so they can be tested without rendering
    // this page. See src/common/activityDrafts.ts.
    const {
      ok,
      error: submitError,
      failedUploads,
      remainingAttachmentsByDraft,
    } = await submitActivityDrafts<ActivityFormValues, AttachmentItem>({
      drafts,
      draftKeys,
      attachmentsByDraft: draftAttachments,
      createdActivityIds: createdActivityIdsRef.current,
      validateDraft: draft =>
        createActivitySchema.validateSync(draft, {
          context: { isUnionSelected, isAdvisorEditable, isAdvisorRequired },
        }),
      createActivity: draft => {
        const dto = toDTO(draft)
        const dtoToSend: CreateActivityDTO = {
          ...dto,
          bankId: Number(dto.customerId), // ✅ store Union as bankId (e.g. 1)
          customerId: Number(dto.bankId), // ✅ store entity as customerId (e.g. 299)
        }
        return createActivity(dtoToSend)
      },
      resolveActivityId: created => {
        // The create endpoint has returned the id under several shapes over time.
        const body = created as {
          id?: string | number
          activityId?: string | number
          activity?: { id?: string | number }
        } | null
        return body?.id ?? body?.activityId ?? body?.activity?.id
      },
      uploadAttachment: (activityId, file) => uploadActivityAttachments(activityId, file),
      describeUploadError: reason =>
        translateAttachmentError(getErrorMessage(reason, 'Unbekannter Fehler')),
      onUploadError: reason => console.error('Failed to upload attachment', reason),
    })

    try {
      // Re-throw so a draft failure and an unexpected failure in the code below land in the
      // same handler — the classification of the error is identical either way.
      if (!ok) throw submitError

      setWorkloadRefreshKey(prev => prev + 1)

      if (failedUploads.length > 0) {
        // Keep the created activities and the files that failed so the user can retry the
        // uploads without creating duplicate activities. Successful uploads are dropped.
        setDraftAttachments(remainingAttachmentsByDraft)

        const details = failedUploads.map(f => `${f.name}: ${f.message}`).join(' | ')
        toast.error(
          'Anhänge konnten nicht hochgeladen werden',
          `Die Aktivitäten wurden erstellt, aber folgende Anhänge sind fehlgeschlagen — ${details}`,
          { duration: 8000 }
        )
        setStatus({
          type: 'error',
          message: `Die Aktivitäten wurden erstellt, aber folgende Anhänge konnten nicht hochgeladen werden. Bitte versuchen Sie den Upload erneut: ${details}`,
        })
        // Stay on the page with the failed files so the retry is possible.
        return
      }

      createdActivityIdsRef.current = {}
      setDraftAttachments(drafts.map(() => []))
      toast.success('Erstellung der Aktivitäten erfolgreich', '', { duration: 2000 })
      setStatus({ type: 'success', message: 'Aktivitäten erfolgreich erstellt!' })

      if (submitModeRef.current === 'saveAndNew') {
        // keep current selection for next activity
        const lastDraft = drafts[drafts.length - 1] ?? formik.values

        const nextValues: ActivityFormValues = {
          ...makeInitialValues(undefined, false),
          customerId: lastDraft.customerId,
          bankId: lastDraft.bankId,
          date: lastDraft.date instanceof Date ? lastDraft.date : new Date(lastDraft.date),
        }

        formik.resetForm({ values: nextValues })
        setDrafts([nextValues])
        // fresh key: this is a new draft, not the one that was just persisted
        setDraftKeys([nextDraftKey()])
        setDraftAttachments([[]])
        setAttachments([])
        setSelectedDraftIndex(0)
        startTimeTouchedByUserRef.current = false
        endTimeTouchedByUserRef.current = false
        return
      }

      // default: go back
      nav(-1)
    } catch (error) {
      // Drop the attachments that already uploaded successfully before the failure — those
      // activities are persisted and will be skipped on the next attempt, so keeping their
      // files would upload them twice.
      setDraftAttachments(remainingAttachmentsByDraft)

      if (error instanceof Yup.ValidationError) {
        setStatus({ type: 'error', message: 'Bitte überprüfen Sie Ihre Eingaben.' })
        toast.error('Bitte überprüfen Sie Ihre Eingaben.')
      } else {
        console.error(error)
        const message = getErrorMessage(error, 'Ein Fehler ist aufgetreten.')
        setStatus({ type: 'error', message })
        toast.error('Speichern fehlgeschlagen', message)
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleUpdateActivity = async (
    valuesToSend: ActivityFormValues,
    { setSubmitting, setStatus }: FormikHelpers<ActivityFormValues>
  ) => {
    if (!editId) return

    if (isInvoicedActivity) {
      setStatus({
        type: 'error',
        message: INVOICED_ACTIVITY_LOCK_MESSAGE,
      })
      return
    }

    setSubmitting(true)

    try {
      createActivitySchema.validateSync(valuesToSend, {
        context: { isUnionSelected, isAdvisorEditable, isAdvisorRequired },
      })

      const dto = toDTO(valuesToSend)
      const dtoToSend: CreateActivityDTO = storedIsSwapped
        ? { ...dto, bankId: Number(dto.customerId), customerId: Number(dto.bankId) }
        : dto

      await updateActivity(editId, dtoToSend)

      const filesToUpload = attachments.map(a => a.file)
      let attachmentError: unknown = null

      if (filesToUpload.length > 0) {
        try {
          await Promise.all(
            filesToUpload.map(file => uploadActivityAttachments(Number(editId), file))
          )

          setAttachments([])
          try {
            const data = await listActivityAttachments(Number(editId))
            const mapped: ExistingAttachmentItem[] = (data.attachments ?? []).map((item: any) => ({
              id: item.id,
              name: item.fileName ?? item.filename ?? item.originalFileName ?? 'Anhang',
              size: item.size ?? item.fileSize,
              downloadUrl:
                item.downloadUrl ??
                item.url ??
                item.link ??
                `${API_BASE_URL}/activities/${editId}/attachments/${item.id}/download`,
            }))
            setExistingAttachments(mapped)
          } catch (error) {
            console.error('Failed to refresh attachments after upload', error)
          }
        } catch (error) {
          console.error('Failed to upload attachments', error)
          attachmentError = error
        }
      }

      if (attachmentError) {
        const reason = translateAttachmentError(
          getErrorMessage(attachmentError, 'Unbekannter Fehler')
        )
        const message = `Die Aktivität wurde gespeichert, aber die Anhänge konnten nicht hochgeladen werden: ${reason}`
        toast.error('Anhänge konnten nicht hochgeladen werden', message, { duration: 8000 })
        setStatus({ type: 'error', message })
      } else {
        toast.success('Aktivität erfolgreich gespeichert', '', { duration: 2000 })
        setStatus({ type: 'success', message: 'Aktivität erfolgreich gespeichert!' })
      }

      if (submitModeRef.current === 'saveAndNew') {
        const nextValues: ActivityFormValues = {
          ...makeInitialValues(undefined, false),
          customerId: valuesToSend.customerId,
          bankId: valuesToSend.bankId,
          date:
            valuesToSend.date instanceof Date ? valuesToSend.date : new Date(valuesToSend.date),
        }

        nav('/activities/new', {
          state: {
            values: nextValues,
            customers,
            banks,
            tags: props.tags ?? state.tags ?? [],
            services: props.services ?? resolvedServices,
            acceptedOrders,
            editId: undefined,
          },
        })

        return
      }

      nav(-1)
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        setStatus({ type: 'error', message: 'Bitte überprüfen Sie Ihre Eingaben.' })
        toast.error('Bitte überprüfen Sie Ihre Eingaben.')
      } else if (isInvoicedActivityConflict(error)) {
        const message = getInvoicedActivityConflictMessage(error)

        toast.error('Aktivität kann nicht bearbeitet werden', message, { duration: 4000 })
        setStatus({ type: 'error', message })
        nav(-1)
      } else {
        console.error(error)
        const message = getErrorMessage(error, 'Ein Fehler ist aufgetreten.')
        setStatus({ type: 'error', message })
        toast.error('Speichern fehlgeschlagen', message)
      }
    } finally {
      setSubmitting(false)
    }
  }
  const isInvoicedActivity = Boolean((values as any)?.invoiced)
  const isReadOnly = Boolean(editId && isInvoicedActivity)

  const isCreate = !editId

  const handleDraftChange = (field: keyof ActivityFormValues, value: any) => {
    if (isReadOnly) return

    const updatedDrafts = [...drafts]
    updatedDrafts[selectedDraftIndex] = {
      ...updatedDrafts[selectedDraftIndex],
      [field]: value,
    }
    syncDraftsValidity(updatedDrafts)
  }

  const handleDraftLocationChange = (field: keyof ActivityFormValues['location'], value: any) => {
    if (isReadOnly) return

    const updatedDrafts = [...drafts]
    updatedDrafts[selectedDraftIndex] = {
      ...updatedDrafts[selectedDraftIndex],
      location: {
        ...updatedDrafts[selectedDraftIndex].location,
        [field]: value,
      },
    }
    syncDraftsValidity(updatedDrafts)
  }

  const handleServicesChange = (services: ServiceRow[]) => {
    if (isReadOnly) return
    const primary = services[0]
    const primaryCode = primary?.serviceCode ?? ''

    // keep CreateActivityDTO "type" in sync (it stores service CODE)
    formik.setFieldValue('services', services, true)
    formik.setFieldValue('type', primaryCode, true)
    formik.setFieldValue(
      'timeQuantity',
      primary && primary.quantity !== '' ? Number(primary.quantity) || 0 : 0,
      true
    )

    const updatedDrafts = [...drafts]
    updatedDrafts[selectedDraftIndex] = {
      ...updatedDrafts[selectedDraftIndex],
      services,
      type: primaryCode as any,
      timeQuantity: primary && primary.quantity !== '' ? Number(primary.quantity) || 0 : 0,
    }
    syncDraftsValidity(updatedDrafts)

    formik.setStatus(null)
    formik.validateForm()
  }

  const handleServiceTypeChange = (index: number, newCode: string) => {
    const current = formik.values.services ?? []
    const next = current.map((row, i) => (i === index ? { ...row, serviceCode: newCode } : row))
    handleServicesChange(next)
  }

  const handleServiceQuantityChange = (index: number, raw: string) => {
    const code = formik.values.services?.[index]?.serviceCode ?? formik.values.type
    const snapped = clampAndSnapQty(raw, code)

    const current = formik.values.services ?? []
    const next = current.map((row, i) =>
      i === index ? { ...row, quantity: snapped as number | '' } : row
    )

    handleServicesChange(next)
  }

  const handleAddAttachments = (files: FileList | null) => {
    if (isReadOnly) return
    if (!files || !files.length) return

    // Count what is already attached so the per-entity limit is respected.
    const existingCount = editId
      ? existingAttachments.length + attachments.length
      : (draftAttachments[selectedDraftIndex] ?? []).length

    const result = validateAttachments(Array.from(files), existingCount)

    const rejectionMessage = describeRejectedAttachments(result)
    if (rejectionMessage) {
      toast.error('Anhang nicht möglich', rejectionMessage)
    }

    if (result.accepted.length === 0) return

    const newItems: AttachmentItem[] = result.accepted.map(file => ({
      id: `${file.name}-${file.lastModified}-${Math.random().toString(36).slice(2, 8)}`,
      file,
    }))

    if (editId) {
      // edit mode: one activity
      setAttachments(prev => [...prev, ...newItems])
    } else {
      // create mode: per-draft attachments
      setDraftAttachments(prev => {
        const next = [...prev]
        const current = next[selectedDraftIndex] ?? []
        next[selectedDraftIndex] = [...current, ...newItems]
        return next
      })
    }
  }

  const handleRemoveAttachment = (id: string) => {
    if (isReadOnly) return
    if (editId) {
      setAttachments(prev => prev.filter(item => item.id !== id))
    } else {
      setDraftAttachments(prev => {
        const next = [...prev]
        const current = next[selectedDraftIndex] ?? []
        next[selectedDraftIndex] = current.filter(item => item.id !== id)
        return next
      })
    }
  }

  const currentAttachments = editId ? attachments : (draftAttachments[selectedDraftIndex] ?? [])

  // const isSubmitDisabled = !editId
  // ? draftValidities.some(valid => !valid)
  // : !formik.isValid || !formik.dirty
  // Identify Union Investment as the special customer (Auftraggeber)

  // Banks visible for current customer selection
  const availableBanks = useMemo(() => {
    const selectedCustomerId = (formik.values as any).customerId

    if (!selectedCustomerId) return []

    // If Union is selected → show the banks prop (service providers)
    if (unionCustomer && Number(selectedCustomerId) === Number(unionCustomer.value)) {
      return banks
    }

    // Non-Union → Leistungsempfänger = selected customer (shouldn't typically happen)
    const customer = customers.find(c => Number(c.value) === Number(selectedCustomerId))
    return customer ? [{ label: customer.label, value: String(customer.value) }] : []
  }, [banks, customers, unionCustomer, formik.values.customerId])

  const [bankSearchInput, setBankSearchInput] = useState('')

  // Keep the input showing the selected bank's name once a selection is made
  // (and clear it again whenever the selection is cleared/changes upstream).
  const selectedBankLabel = useMemo(() => {
    const selected = availableBanks.find(b => String(b.value) === String(formik.values.bankId))
    return selected?.label ?? ''
  }, [availableBanks, formik.values.bankId])

  useEffect(() => {
    setBankSearchInput(selectedBankLabel)
  }, [selectedBankLabel])

  const availableBankItems = useMemo(() => {
    const query = bankSearchInput.trim().toLowerCase()

    const filteredBanks = query
      ? availableBanks.filter(b => {
          const label = String(b.label ?? '').toLowerCase()
          const postal = (b.postal ?? '').toLowerCase()
          const blzNum = (b.blzNum ?? '').toLowerCase()
          const accountNumber = (b.accountNumber ?? '').toLowerCase()

          return (
            label.includes(query) ||
            postal.includes(query) ||
            blzNum.includes(query) ||
            accountNumber.includes(query)
          )
        })
      : availableBanks

    // Collection item values must match the Combobox's controlled `value`
    // (string[]), otherwise Zag-js can't sync the selected item back to the
    // input's display text.
    const items = filteredBanks.map(b => ({ ...b, value: String(b.value) }))

    return createListCollection({ items })
  }, [availableBanks, bankSearchInput])

  const isUnionSelected =
    !!unionCustomer && Number(formik.values.customerId) === Number(unionCustomer.value)

  const selectedServiceCodeRaw = (formik.values.services?.[0]?.serviceCode ?? '').toUpperCase()
  const currentCode = formik.values.services?.[0]?.serviceCode ?? formik.values.type
  const isPilotSelected = normalizeSvc(currentCode) === 'PILOT'
  const { editable: isAdvisorEditable, required: isAdvisorRequired } = getAdvisorNumberState(
    isUnionSelected,
    currentCode
  )
  const [advisorSearchInput, setAdvisorSearchInput] = useState('')
  const [contactBookAdvisors, setContactBookAdvisors] = useState<ContactBookDto[]>([])
  const [advisorLoading, setAdvisorLoading] = useState(false)
  const [advisorLoadError, setAdvisorLoadError] = useState<string | null>(null)
  const { min, step, max } = getQtyConstraints(currentCode)
  const selectedServiceForCapacity = normalizeServiceForBilling(currentCode)
  const qtyHint = getQtyHint(currentCode)
  const enteredQty = useMemo(() => {
    const raw = formik.values.services?.[0]?.quantity ?? formik.values.timeQuantity
    if (raw === '' || raw == null) return 0
    const n = Number(raw)
    return Number.isFinite(n) ? n : 0
  }, [formik.values.services, formik.values.timeQuantity])

  useEffect(() => {
    if (editId) return
    if (!isUnionSelected) return

    const serviceCode = formik.values.services?.[0]?.serviceCode ?? formik.values.type
    const qtyRaw = formik.values.services?.[0]?.quantity ?? formik.values.timeQuantity
    const qty = qtyRaw === '' ? undefined : Number(qtyRaw)

    const durationMinutes = getAutoDurationMinutes(serviceCode, qty, isUnionSelected)
    if (!durationMinutes) return

    let startValue = formik.values.activityStartTime ?? ''
    const hasStart = !!startValue

    if (!hasStart && !startTimeTouchedByUserRef.current) {
      const roundedNow = roundUpToNextQuarterHour()
      startValue = formatTimeHHmm(roundedNow)

      formik.setFieldValue('activityStartTime', startValue, false)
      handleDraftChange('activityStartTime' as any, startValue)
    }

    if (startValue && !endTimeTouchedByUserRef.current) {
      const nextEnd = addMinutesToHHmm(startValue, durationMinutes)

      if (formik.values.activityEndTime !== nextEnd) {
        formik.setFieldValue('activityEndTime', nextEnd, false)
        handleDraftChange('activityEndTime' as any, nextEnd)
      }
    }
  }, [
    editId,
    isUnionSelected,
    formik.values.services?.[0]?.serviceCode,
    formik.values.services?.[0]?.quantity,
    formik.values.type,
    formik.values.timeQuantity,
    formik.values.activityStartTime,
  ])

  useEffect(() => {
    if (!isUnionSelected) return

    const noStart = !formik.values.activityStartTime
    const noEnd = !formik.values.activityEndTime

    if (noStart) {
      startTimeTouchedByUserRef.current = false
    }

    if (noEnd) {
      endTimeTouchedByUserRef.current = false
    }
  }, [isUnionSelected, formik.values.activityStartTime, formik.values.activityEndTime])

  useEffect(() => {
    if (editId) return
    if (!isUnionSelected) return
    if (formik.values.bankId) return

    const first = availableBanks[0]
    if (!first) return

    const nextId = Number(first.value)
    formik.setFieldValue('bankId', nextId, false)
    handleDraftChange('bankId', nextId)
  }, [editId, isUnionSelected, availableBanks, formik.values.bankId])

  useEffect(() => {
    if (isAdvisorEditable) return
    if (!formik.values.advisorNumber && !formik.values.contactBookId && !advisorSearchInput) return

    formik.setFieldValue('advisorNumber', '', false)
    formik.setFieldValue('contactBookId', undefined, false)
    handleDraftChange('advisorNumber' as any, '')
    handleDraftChange('contactBookId' as any, undefined)
    setAdvisorSearchInput('')
  }, [isAdvisorEditable])

  useEffect(() => {
    if (!isUnionSelected || !user?.id || !selectedEntityId) {
      setContactBookAdvisors([])
      setAdvisorLoadError(null)
      setAdvisorLoading(false)
      return
    }

    let cancelled = false

    ;(async () => {
      try {
        setAdvisorLoading(true)
        setAdvisorLoadError(null)

        const result = await getContactBooks({
          userId: user.id,
          bankId: 1,
          customerId: selectedEntityId,
          page: 1,
          pageSize: 100,
        })

        if (!cancelled) {
          setContactBookAdvisors(result.items ?? [])
        }
      } catch (error) {
        console.error('Failed to load advisors for activity', error)
        if (!cancelled) {
          setContactBookAdvisors([])
          setAdvisorLoadError('Berater konnten nicht geladen werden.')
        }
      } finally {
        if (!cancelled) setAdvisorLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [isUnionSelected, user?.id, selectedEntityId])

  const selectedAdvisor = useMemo(() => {
    const contactBookId = Number((formik.values as any).contactBookId || 0)
    if (!contactBookId) return null

    const advisorNumber = digitsOnly((formik.values as any).advisorNumber ?? '').slice(0, 8)
    if (!advisorNumber) return null
    return (
      contactBookAdvisors.find(advisor => Number(advisor.id) === contactBookId) ??
      contactBookAdvisors.find(
        advisor =>
          advisor.advisorNumber === advisorNumber ||
          digitsOnly(advisor.advisorNumber) === advisorNumber
      ) ??
      null
    )
  }, [contactBookAdvisors, formik.values.advisorNumber, formik.values.contactBookId])

  useEffect(() => {
    const advisorNumber = digitsOnly((formik.values as any).advisorNumber ?? '').slice(0, 8)

    if (selectedAdvisor) {
      setAdvisorSearchInput(formatAdvisorLabel(selectedAdvisor))
      return
    }

    setAdvisorSearchInput(advisorNumber)
  }, [selectedAdvisor, formik.values.advisorNumber])

  const advisorItems = useMemo(() => {
    const query = advisorSearchInput.trim().toLowerCase()
    const queryDigits = digitsOnly(query)
    const manualAdvisorNumber = queryDigits.slice(0, 8)
    const paddedManualAdvisorNumber = manualAdvisorNumber
      ? padAdvisorNumber(manualAdvisorNumber)
      : ''

    const filtered = query
      ? contactBookAdvisors.filter(advisor => {
          const fullName = [advisor.firstName, advisor.lastName]
            .filter(Boolean)
            .join(' ')
            .toLowerCase()
          const advisorNumber = advisor.advisorNumber.toLowerCase()

          return (
            fullName.includes(query) ||
            advisorNumber.includes(query) ||
            (!!queryDigits && digitsOnly(advisorNumber).includes(queryDigits))
          )
        })
      : contactBookAdvisors

    const hasMatchingAdvisorNumber =
      !!manualAdvisorNumber &&
      contactBookAdvisors.some(advisor => {
        const normalized = digitsOnly(advisor.advisorNumber)
        return normalized === manualAdvisorNumber || normalized === paddedManualAdvisorNumber
      })

    const manualItem: AdvisorComboboxItem[] =
      manualAdvisorNumber && !hasMatchingAdvisorNumber
        ? [
            {
              label: `Beraternummer ${manualAdvisorNumber} übernehmen`,
              value: `manual:${manualAdvisorNumber}`,
              manualAdvisorNumber,
            },
          ]
        : []

    return createListCollection({
      items: [
        ...manualItem,
        ...filtered.map(advisor => ({
          label: formatAdvisorLabel(advisor),
          value: advisor.advisorNumber,
          advisor,
        })),
      ],
    })
  }, [advisorSearchInput, contactBookAdvisors])

  const originalQty = useMemo(() => {
    const raw = initialValues.services?.[0]?.quantity ?? initialValues.timeQuantity
    if (raw === '' || raw == null) return 0
    const n = Number(raw)
    return Number.isFinite(n) ? n : 0
  }, [initialValues])

  const remainingAfterSelection = useMemo(() => {
    if (!isUnionSelected || !selectedEntityId || !workload) return null

    const available = Number(workload[selectedServiceForCapacity as keyof typeof workload] ?? 0)

    if (editId) {
      return available - Math.max(0, enteredQty - originalQty)
    }

    return available - enteredQty
  }, [
    isUnionSelected,
    selectedEntityId,
    workload,
    selectedServiceForCapacity,
    enteredQty,
    editId,
    originalQty,
  ])

  function formatQty2(value: number) {
    return Number(value.toFixed(2)).toString().replace('.', ',')
  }

  const hasInsufficientCapacity =
    isUnionSelected &&
    selectedEntityId > 0 &&
    remainingAfterSelection != null &&
    remainingAfterSelection < 0

  const disableSubmit =
    isReadOnly ||
    formik.isSubmitting ||
    !formik.isValid ||
    hasInsufficientCapacity ||
    (!isCreate && !formik.dirty)
  const remainingLabel = useMemo(() => {
    if (remainingAfterSelection == null) return ''

    const formatted = formatQty2(remainingAfterSelection)
    const svc = selectedServiceForCapacity

    if (svc === 'TOJ') {
      return `${formatted} Stück offen`
    }
    if (svc === 'CONSULTING') {
      return `${formatted} Stunden offen`
    }
    if (svc === 'TRAINING') {
      return `${formatted} Tage offen`
    }
    if (svc === 'PILOT') {
      return `${formatted} Einheiten offen`
    }
    return `${formatted} Verbleibend`
  }, [remainingAfterSelection, selectedServiceForCapacity])

  const remainingIsNegative = (remainingAfterSelection ?? 0) < 0

  useEffect(() => {
    formik.validateForm()
  }, [isUnionSelected])

  useEffect(() => {
    if (!isUnionSelected) {
      setWorkload(null)
      setWorkloadError(null)
      return
    }

    if (!user?.id) return
    if (!selectedEntityId) return

    let cancelled = false

    ;(async () => {
      try {
        setWorkloadLoading(true)
        setWorkloadError(null)

        const result = await getWorkload({
          userId: user.id,
          bankId: 1,
          customerId: selectedEntityId,
          from,
          to,
        })

        const map: Record<string, number> = { TOJ: 0, CONSULTING: 0, TRAINING: 0, PILOT: 0 }
        result.buckets.forEach(b => {
          map[b.serviceType] = hoursToUnits(b.serviceType, b.remainingApprovedOrderHours)
        })

        if (!cancelled) setWorkload(map)
      } catch (e: any) {
        if (!cancelled) setWorkloadError(e?.message ?? 'Kontingente konnten nicht geladen werden')
        if (!cancelled) setWorkload(null)
      } finally {
        if (!cancelled) setWorkloadLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [isUnionSelected, user?.id, selectedEntityId, from, to, workloadRefreshKey])

  useEffect(() => {
    if (!isUnionSelected) return

    const code = formik.values.services?.[0]?.serviceCode ?? formik.values.type
    const qtyRaw = formik.values.services?.[0]?.quantity ?? formik.values.timeQuantity
    const qty = qtyRaw === '' ? undefined : Number(qtyRaw)
    const cancelled = Boolean((formik.values as any).shortNoticeCancelled)

    const next = computeBillingVariant({
      serviceCode: code,
      qty,
      shortNoticeCancelled: cancelled,
    })

    if (formik.values.billingVariant !== next) {
      formik.setFieldValue('billingVariant', next, false)
      handleDraftChange('billingVariant' as any, next)
    }
  }, [
    isUnionSelected,
    (formik.values as any).shortNoticeCancelled,
    formik.values.services?.[0]?.serviceCode,
    formik.values.services?.[0]?.quantity,
    formik.values.timeQuantity,
  ])

  const billingVariantKey =
    selectedServiceCodeRaw === 'TOJ_CUSTOMER' ? 'TOJ' : selectedServiceCodeRaw

  const showBillingVariant =
    isUnionSelected &&
    (billingVariantKey === 'TOJ' ||
      billingVariantKey === 'TRAINING' ||
      billingVariantKey === 'CONSULTING')

  const billingVariantOptions = useMemo(() => {
    return BILLING_VARIANTS_BY_TYPE[billingVariantKey] ?? []
  }, [billingVariantKey])

  useEffect(() => {
    if (!isUnionSelected) {
      if (formik.values.orderId) {
        formik.setFieldValue('orderId', undefined, false)
        handleDraftChange('orderId' as any, undefined)
      }
      lastNoOrderKeyRef.current = ''
      return
    }

    const selectedEntityId = Number(formik.values.bankId)
    const selectedServiceCode = formik.values.services?.[0]?.serviceCode ?? formik.values.type

    if (!selectedEntityId || !selectedServiceCode) return
    if (!acceptedOrders?.length) return // <-- important: don't try to resolve before orders exist
    const selectedServiceCodeRaw = (
      formik.values.services?.[0]?.serviceCode ??
      formik.values.type ??
      ''
    )
      .toString()
      .trim()
      .toUpperCase()

    const selectedServiceCodeForOrder =
      selectedServiceCodeRaw === 'TOJ_CUSTOMER' ? 'TOJ' : selectedServiceCodeRaw

    const orderId = resolveApprovedOrderId({
      acceptedOrders,
      unionId,
      selectedEntityId,
      selectedServiceCode: selectedServiceCodeForOrder,
    })

    // const orderId = resolveApprovedOrderId({
    //   acceptedOrders,
    //   unionId,
    //   selectedEntityId,
    //   selectedServiceCode,
    // })

    const normalizedOrderId = orderId && Number.isFinite(orderId) ? Number(orderId) : undefined
    formik.setFieldValue('orderId', normalizedOrderId, false)
    handleDraftChange('orderId' as any, normalizedOrderId)

    if (!orderId) {
      const key = `${selectedEntityId}::${selectedServiceCodeForOrder}`
      if (lastNoOrderKeyRef.current !== key) {
        lastNoOrderKeyRef.current = key

        // schedule toast outside lifecycle -> removes flushSync warning
        setTimeout(() => {
          toast.warning(
            'Keine passende Bestellung gefunden',
            'Für die gewählte Leistung und den Leistungsempfänger wurde keine freigegebene Bestellung gefunden. Die Aktivität wird ohne orderId gespeichert (aktuell nur Warnung).'
          )
        }, 0)
      }
    } else {
      lastNoOrderKeyRef.current = ''
    }
  }, [
    isUnionSelected,
    formik.values.bankId,
    formik.values.services?.[0]?.serviceCode,
    formik.values.type,
    acceptedOrders,
    unionId,
  ])

  const fixedQuantityByVariant: Partial<Record<BillingVariant, number>> = {
    HALF_TRAINING_DAY: 0.5,
    HALF_HOUR_CONSULTING: 0.5,
  }

  useEffect(() => {
    if (!showBillingVariant) return

    const v = formik.values.billingVariant as BillingVariant | undefined
    if (!v) return

    const fixed = fixedQuantityByVariant[v]
    if (fixed == null) return

    const current = formik.values.services ?? []
    const first = current[0]
    if (!first) return

    // Avoid endless loops: only update if different
    const currentQty = first.quantity === '' ? null : Number(first.quantity)
    if (currentQty === fixed && Number(formik.values.timeQuantity) === fixed) return

    const nextServices = [{ ...first, quantity: fixed }, ...current.slice(1)]
    handleServicesChange(nextServices) // updates services + type + timeQuantity + drafts
  }, [showBillingVariant, formik.values.billingVariant])

  useEffect(() => {
    if (!showBillingVariant) return
    const allowed = billingVariantOptions.map(o => o.value)
    const current = (formik.values.billingVariant ?? 'STANDARD') as BillingVariant

    if (!allowed.includes(current)) {
      formik.setFieldValue('billingVariant', 'STANDARD', true)
      handleDraftChange('billingVariant' as any, 'STANDARD')
    }
  }, [showBillingVariant, billingVariantOptions, formik.values.billingVariant])
  useEffect(() => {
    setTojConfirmed(false)
  }, [formik.values.date, formik.values.timeQuantity, formik.values.services?.[0]?.serviceCode])
  useEffect(() => {
    formik.validateForm()
  }, [
    formik.values.date,
    formik.values.customerId,
    formik.values.bankId,
    formik.values.type,
    formik.values.timeQuantity,
    formik.values.location?.type,
    formik.values.location?.city,
    formik.values.activityStartTime,
    formik.values.activityEndTime,
    formik.values.advisorNumber,
    isUnionSelected,
  ])

  const handleLogout = async () => {
    // Central teardown (SIB-924): server logout + full client session reset,
    // including the user store. Local cleanup happens even if the server call fails.
    await endUserSession()
    navigate('/login', { replace: true })
  }

  return (
    <SibPageShell
      userName={displayName}
      onNewOrder={handleNewOrder}
      onAddActivity={handleAddActivity}
      onCreateInvoice={handleCreateInvoice}
      onGoDashboard={() => nav(dashboardPath)}
      onOpenSupport={() => window.open(SUPPORT_PORTAL_URL, '_blank')}
      onLogout={handleLogout}
    >
      <Page.Root hasActionBar>
        <Page.Header
          breadcrumbs={[
            { label: 'Dashboard', to: dashboardPath },
            { label: 'Aktivitäten' },
            { label: editId ? 'Bearbeiten' : 'Neue Aktivität' },
          ]}
          onBack={() => nav(-1)}
          title={editId ? 'Aktivität bearbeiten' : 'Neue Aktivität erfassen'}
          titleAddon={
            isInvoicedActivity ? (
              <Badge colorPalette="red" variant="subtle">
                Bereits in Rechnung gestellt
              </Badge>
            ) : undefined
          }
          meta={dayjs(formik.values.date).format('DD.MM.YYYY')}
        />

        <Page.Content>
          {/* Page layout */}
          <Page.ContextColumns>
            {/* Left: Form card */}
            <Page.Main>
              <Card.Root>
                <Card.Body>
                  <form onSubmit={formik.handleSubmit} style={{ height: '100%', width: '100%' }}>
                    <FormLayout.Root>
                      {/* Auftraggeber / Leistungsempfänger */}
                      <FormLayout.Fields>
                        <Field.Root
                          minW={0}
                          gridColumn={{ base: 'auto', md: 'span 6' }}
                          invalid={!!(formik.touched.customerId && formik.errors.customerId)}
                        >
                          <Field.Label>
                            Auftraggeber <Field.RequiredIndicator />
                          </Field.Label>
                          <Select.Root
                            collection={customerItems}
                            disabled={isReadOnly}
                            value={
                              formik.values.customerId !== undefined
                                ? ([formik.values.customerId] as any)
                                : []
                            }
                            onBlur={() => formik.setFieldTouched('customerId', true, true)}
                            onValueChange={({ value }) => {
                              const selectedValue = value[0] ? Number(value[0]) : 0
                              formik.setFieldValue('customerId', selectedValue, true)
                              handleDraftChange('customerId', selectedValue)
                              formik.setFieldValue('advisorNumber', '', true)
                              handleDraftChange('advisorNumber' as any, '')
                              formik.setFieldValue('contactBookId', undefined, true)
                              handleDraftChange('contactBookId' as any, undefined)
                              setAdvisorSearchInput('')
                              formik.setStatus(null)
                              if (
                                unionCustomer &&
                                Number(selectedValue) === Number(unionCustomer.value) &&
                                banks.length === 0
                              ) {
                                toast.info(
                                  'Keine Leistungsempfänger verfügbar',
                                  'Es werden nur Leistungsempfänger angezeigt, die in einer freigegebenen Bestellung ausgewählt wurden. Geben Sie zuerst eine Bestellung auf oder warten Sie auf die Freigabe durch die Union.',
                                  { duration: 5000 }
                                )
                              }
                              if (
                                unionCustomer &&
                                Number(selectedValue) === Number(unionCustomer.value)
                              ) {
                                const currentBankId = formik.values.bankId
                                const stillAllowed = banks.some(
                                  b => Number(b.value) === Number(currentBankId)
                                )

                                if (!stillAllowed) {
                                  // const first = banks[0]
                                  // const nextId = first ? Number(first.value) : 0
                                  // formik.setFieldValue('bankId', nextId)
                                  // handleDraftChange('bankId', nextId)
                                  const first = banks[0]

                                  if (first) {
                                    const nextId = Number(first.value)
                                    formik.setFieldValue('bankId', nextId, true)
                                    handleDraftChange('bankId', nextId)
                                  } else {
                                    // no Leistungsempfänger available yet
                                    formik.setFieldValue('bankId', undefined, true)
                                    handleDraftChange('bankId', undefined)
                                  }
                                }
                              } else {
                                formik.setFieldValue('bankId', selectedValue, true)
                                handleDraftChange('bankId', selectedValue)
                              }
                            }}
                          >
                            <Select.HiddenSelect />
                            <Select.Control>
                              <Select.Trigger>
                                <Select.ValueText placeholder="Bitte auswählen" />
                              </Select.Trigger>
                              <Select.IndicatorGroup>
                                <Select.Indicator />
                                <Select.ClearTrigger />
                              </Select.IndicatorGroup>
                            </Select.Control>
                            <Select.Positioner>
                              <Select.Content>
                                {customerItems.items.map(customer => (
                                  <Select.Item item={customer} key={customer.value}>
                                    {customer.label}
                                    <Select.ItemIndicator />
                                  </Select.Item>
                                ))}
                              </Select.Content>
                            </Select.Positioner>
                          </Select.Root>
                          {formik.touched.customerId && formik.errors.customerId && (
                            <Field.ErrorText>{formik.errors.customerId}</Field.ErrorText>
                          )}
                        </Field.Root>

                        <Field.Root
                          minW={0}
                          gridColumn={{ base: 'auto', md: 'span 6' }}
                          invalid={!!(formik.touched.bankId && formik.errors.bankId)}
                        >
                          <Field.Label>
                            Leistungsempfänger <Field.RequiredIndicator />
                          </Field.Label>

                          <Combobox.Root
                            invalid={!!(formik.touched.bankId && formik.errors.bankId)}
                            collection={availableBankItems}
                            inputValue={bankSearchInput}
                            onInputValueChange={e => setBankSearchInput(e.inputValue)}
                            onOpenChange={({ open }) => {
                              if (open) setBankSearchInput('')
                            }}
                            value={
                              formik.values.bankId !== undefined
                                ? [String(formik.values.bankId)]
                                : []
                            }
                            disabled={availableBanks.length === 0 || isReadOnly}
                            onValueChange={({ value }) => {
                              const selected = value[0] ? Number(value[0]) : 0
                              formik.setFieldValue('bankId', selected, true)
                              handleDraftChange('bankId', selected)
                              formik.setFieldValue('advisorNumber', '', true)
                              handleDraftChange('advisorNumber' as any, '')
                              formik.setFieldValue('contactBookId', undefined, true)
                              handleDraftChange('contactBookId' as any, undefined)
                              setAdvisorSearchInput('')
                              formik.setStatus(null)
                            }}
                          >
                            <Combobox.Control>
                              <Combobox.Input placeholder="Suchen..." />
                              <Combobox.IndicatorGroup>
                                {isUnionSelected && (
                                  <Combobox.ClearTrigger
                                    onClick={() => {
                                      formik.setFieldValue('bankId', undefined, true)
                                      handleDraftChange('bankId', undefined)
                                      formik.setFieldValue('advisorNumber', '', true)
                                      handleDraftChange('advisorNumber' as any, '')
                                      formik.setFieldValue('contactBookId', undefined, true)
                                      handleDraftChange('contactBookId' as any, undefined)
                                      setAdvisorSearchInput('')
                                    }}
                                  />
                                )}
                                <Combobox.Trigger />
                              </Combobox.IndicatorGroup>
                            </Combobox.Control>
                            <Portal>
                              <Combobox.Positioner style={{ zIndex: 10000 }} zIndex={10000}>
                                <Combobox.Content>
                                  <Combobox.Empty>
                                    Keine Leistungsempfänger gefunden
                                  </Combobox.Empty>
                                  {availableBankItems.items.map(bank => (
                                    <Combobox.Item item={bank} key={bank.value}>
                                      {bank.label}
                                      <Combobox.ItemIndicator />
                                    </Combobox.Item>
                                  ))}
                                </Combobox.Content>
                              </Combobox.Positioner>
                            </Portal>
                          </Combobox.Root>

                          {formik.touched.customerId && formik.errors.customerId && (
                            <Field.ErrorText>{formik.errors.customerId}</Field.ErrorText>
                          )}
                          {formik.touched.bankId && formik.errors.bankId && (
                            <Field.ErrorText>{String(formik.errors.bankId)}</Field.ErrorText>
                          )}
                        </Field.Root>

                        {isUnionSelected && availableBankItems.items.length === 0 && (
                          <Alert.Root
                            gridColumn={{ base: 'auto', md: '1 / -1' }}
                            status="warning"
                            variant="surface"
                            colorPalette="yellow"
                          >
                            <Alert.Indicator />
                            <Alert.Content>
                              <Alert.Title>Keine freigegebene Bestellung</Alert.Title>
                              <Alert.Description>
                                Um einen Leistungsempfänger auszuwählen, muss zuerst eine
                                Bestellung für diesen freigegeben werden.
                              </Alert.Description>
                            </Alert.Content>
                          </Alert.Root>
                        )}

                        {/* Art & Menge der Leistungen */}
                        {(formik.values.services ?? []).map((service, index) => (
                          <Box key={service.id} display="contents">
                            <Field.Root
                              minW={0}
                              gridColumn={{
                                base: 'auto',
                                md: isUnionSelected ? 'span 3' : 'span 6',
                              }}
                              invalid={
                                !!(formik.touched.timeQuantity && formik.errors.timeQuantity)
                              }
                            >
                              <Field.Label>
                                Leistungsart <Field.RequiredIndicator />
                              </Field.Label>
                              <Select.Root
                                disabled={isReadOnly}
                                collection={availableServiceTypes}
                                value={service.serviceCode ? [service.serviceCode] : []}
                                onValueChange={({ value }) =>
                                  handleServiceTypeChange(index, value[0] || '')
                                }
                              >
                                <Select.HiddenSelect />
                                <Select.Control>
                                  <Select.Trigger>
                                    <Select.ValueText placeholder="Leistung auswählen" />
                                  </Select.Trigger>
                                  <Select.IndicatorGroup>
                                    <Select.Indicator />
                                    <Select.ClearTrigger />
                                  </Select.IndicatorGroup>
                                </Select.Control>
                                <Select.Positioner>
                                  <Select.Content>
                                    {availableServiceTypes.items.map(svc => (
                                      <Select.Item item={svc} key={svc.value}>
                                        {svc.label}
                                        <Select.ItemIndicator />
                                      </Select.Item>
                                    ))}
                                  </Select.Content>
                                </Select.Positioner>
                              </Select.Root>
                            </Field.Root>

                            <Field.Root
                              minW={0}
                              gridColumn={{
                                base: 'auto',
                                md: isUnionSelected ? 'span 3' : 'span 6',
                              }}
                            >
                              <Field.Label>
                                Menge <Field.RequiredIndicator />
                                {qtyHint && (
                                  <Tooltip
                                    content={
                                      <Box maxW="250px">
                                        <Text textStyle="metadata">{qtyHint}</Text>
                                      </Box>
                                    }
                                  >
                                    <chakra.span color="fg.muted" opacity="0.8" cursor="pointer">
                                      <LuInfo size={12} />
                                    </chakra.span>
                                  </Tooltip>
                                )}
                              </Field.Label>

                              <SettingsInput
                                min={min}
                                step={step}
                                max={max}
                                type="number"
                                inputMode="decimal"
                                disabled={isReadOnly}
                                autoComplete="off"
                                value={service.quantity === '' ? '' : service.quantity}
                                onKeyDown={e => {
                                  if (['e', 'E', '+', '-'].includes(e.key)) {
                                    e.preventDefault()
                                  }
                                }}
                                onPaste={e => {
                                  const pasted = e.clipboardData.getData('text').trim()
                                  if (!/^\d*([.,]\d*)?$/.test(pasted)) {
                                    e.preventDefault()
                                  }
                                }}
                                onChange={e => {
                                  let raw = e.target.value

                                  raw = raw.replace(/[eE+-]/g, '')
                                  raw = raw.replace(',', '.')

                                  const firstDot = raw.indexOf('.')
                                  if (firstDot !== -1) {
                                    raw =
                                      raw.slice(0, firstDot + 1) +
                                      raw.slice(firstDot + 1).replace(/\./g, '')
                                  }

                                  handleServiceQuantityChange(index, raw)
                                }}
                                onBlur={e => {
                                  const normalized = e.target.value
                                    .replace(',', '.')
                                    .replace(/[eE+-]/g, '')
                                  const snapped = clampAndSnapQty(normalized, currentCode)
                                  handleServiceQuantityChange(
                                    index,
                                    snapped === '' ? '' : String(snapped)
                                  )
                                  formik.setFieldTouched('timeQuantity', true, true)
                                }}
                              />

                              {isPilotSelected && (
                                <Status.Root size="sm" colorPalette="green">
                                  <Status.Indicator />
                                  Festpreis: 50,00 € brutto pro Einheit
                                </Status.Root>
                              )}
                              {isUnionSelected && selectedEntityId && workload && currentCode && (
                                <Status.Root
                                  size="sm"
                                  colorPalette={remainingIsNegative ? 'red' : 'green'}
                                >
                                  <Status.Indicator />
                                  {remainingLabel}
                                  {hasInsufficientCapacity && (
                                    <>
                                      {' '}
                                      Für diese Leistung ist kein ausreichendes Kontingent mehr
                                      verfügbar.
                                    </>
                                  )}
                                </Status.Root>
                              )}

                              {formik.touched.timeQuantity && formik.errors.timeQuantity && (
                                <Field.ErrorText>
                                  {String(formik.errors.timeQuantity)}
                                </Field.ErrorText>
                              )}
                            </Field.Root>

                            {isUnionSelected && (
                              <Field.Root
                                minW={0}
                                gridColumn={{ base: 'auto', md: 'span 6' }}
                                required={isAdvisorRequired}
                                invalid={
                                  isAdvisorEditable &&
                                  !!(formik.touched.advisorNumber && formik.errors.advisorNumber)
                                }
                              >
                                <Field.Label>
                                  Berater {isAdvisorRequired && <Field.RequiredIndicator />}
                                </Field.Label>
                                <Flex direction="column" gap={2} w="full">
                                  <Combobox.Root
                                    width="full"
                                    collection={advisorItems}
                                    allowCustomValue
                                    selectionBehavior="preserve"
                                    inputValue={advisorSearchInput}
                                    value={selectedAdvisor ? [selectedAdvisor.advisorNumber] : []}
                                    disabled={
                                      isReadOnly || !selectedEntityId || !isAdvisorEditable
                                    }
                                    onOpenChange={({ open }) => {
                                      if (open && selectedAdvisor) setAdvisorSearchInput('')
                                    }}
                                    onInputValueChange={e => {
                                      const inputValue = e.inputValue
                                      const trimmed = inputValue.trim()
                                      const raw = digitsOnly(trimmed).slice(0, 8)

                                      setAdvisorSearchInput(
                                        /^\d+$/.test(trimmed) ? raw : inputValue
                                      )

                                      if (!trimmed) {
                                        formik.setFieldValue('advisorNumber', '', true)
                                        handleDraftChange('advisorNumber' as any, '')
                                        formik.setFieldValue('contactBookId', undefined, true)
                                        handleDraftChange('contactBookId' as any, undefined)
                                        formik.setStatus(null)
                                        return
                                      }

                                      if (/^\d+$/.test(trimmed)) {
                                        formik.setFieldValue('advisorNumber', raw, true)
                                        handleDraftChange('advisorNumber' as any, raw)
                                        formik.setFieldValue('contactBookId', undefined, true)
                                        handleDraftChange('contactBookId' as any, undefined)
                                      } else if (formik.values.advisorNumber) {
                                        formik.setFieldValue('advisorNumber', '', true)
                                        handleDraftChange('advisorNumber' as any, '')
                                        formik.setFieldValue('contactBookId', undefined, true)
                                        handleDraftChange('contactBookId' as any, undefined)
                                      }

                                      formik.setStatus(null)
                                    }}
                                    onValueChange={({ value }) => {
                                      const selectedValue = value[0] ? String(value[0]) : ''
                                      const manualAdvisorNumber = selectedValue.startsWith(
                                        'manual:'
                                      )
                                        ? selectedValue.replace('manual:', '')
                                        : ''

                                      if (manualAdvisorNumber) {
                                        formik.setFieldValue(
                                          'advisorNumber',
                                          manualAdvisorNumber,
                                          true
                                        )
                                        handleDraftChange(
                                          'advisorNumber' as any,
                                          manualAdvisorNumber
                                        )
                                        formik.setFieldValue('contactBookId', undefined, true)
                                        handleDraftChange('contactBookId' as any, undefined)
                                        setAdvisorSearchInput(manualAdvisorNumber)
                                        formik.setStatus(null)
                                        return
                                      }

                                      const advisor = contactBookAdvisors.find(
                                        item => item.advisorNumber === selectedValue
                                      )
                                      const advisorNumber = advisor?.advisorNumber ?? selectedValue

                                      formik.setFieldValue('advisorNumber', advisorNumber, true)
                                      handleDraftChange('advisorNumber' as any, advisorNumber)
                                      formik.setFieldValue('contactBookId', advisor?.id, true)
                                      handleDraftChange('contactBookId' as any, advisor?.id)
                                      setAdvisorSearchInput(
                                        advisor ? formatAdvisorLabel(advisor) : advisorNumber
                                      )
                                      formik.setStatus(null)
                                    }}
                                  >
                                    <Combobox.Control w="full">
                                      <Combobox.Input
                                        placeholder={
                                          isAdvisorEditable
                                            ? 'Berater auswählen oder Nummer eingeben'
                                            : selectedEntityId
                                              ? 'Nur für TOJ- und Beratungsleistungen verfügbar'
                                              : 'Leistungsempfänger auswählen'
                                        }
                                        w="full"
                                        onBlur={() => {
                                          const raw = digitsOnly(advisorSearchInput).slice(0, 8)

                                          formik.setFieldValue('advisorNumber', raw, true)
                                          handleDraftChange('advisorNumber' as any, raw)
                                          if (
                                            !selectedAdvisor ||
                                            selectedAdvisor.advisorNumber !== raw
                                          ) {
                                            formik.setFieldValue('contactBookId', undefined, true)
                                            handleDraftChange('contactBookId' as any, undefined)
                                          }
                                          formik.setFieldTouched('advisorNumber', true, true)

                                          if (!raw) setAdvisorSearchInput('')
                                        }}
                                      />
                                      <Combobox.IndicatorGroup>
                                        <Combobox.ClearTrigger
                                          onClick={() => {
                                            formik.setFieldValue('advisorNumber', '', true)
                                            handleDraftChange('advisorNumber' as any, '')
                                            formik.setFieldValue('contactBookId', undefined, true)
                                            handleDraftChange('contactBookId' as any, undefined)
                                            setAdvisorSearchInput('')
                                          }}
                                        />
                                        <Combobox.Trigger />
                                      </Combobox.IndicatorGroup>
                                    </Combobox.Control>
                                    <Portal>
                                      <Combobox.Positioner
                                        style={{ zIndex: 10000 }}
                                        zIndex={10000}
                                      >
                                        <Combobox.Content>
                                          <Combobox.Empty>
                                            {advisorLoading
                                              ? 'Berater werden geladen...'
                                              : advisorLoadError || 'Keine Berater vorhanden'}
                                          </Combobox.Empty>
                                          {advisorItems.items.map(item => (
                                            <Combobox.Item item={item} key={item.value}>
                                              <Combobox.ItemText>
                                                {item.manualAdvisorNumber
                                                  ? `Nummer ${item.manualAdvisorNumber} übernehmen`
                                                  : item.label}
                                              </Combobox.ItemText>
                                              <Combobox.ItemIndicator />
                                            </Combobox.Item>
                                          ))}
                                        </Combobox.Content>
                                      </Combobox.Positioner>
                                    </Portal>
                                  </Combobox.Root>
                                </Flex>
                                {isAdvisorEditable &&
                                  formik.touched.advisorNumber &&
                                  formik.errors.advisorNumber && (
                                    <Field.ErrorText>
                                      {String(formik.errors.advisorNumber)}
                                    </Field.ErrorText>
                                  )}
                                <Button asChild variant="plain" colorPalette="blue">
                                  <Link to="/settings/bank/advisors">Kontakte bearbeiten</Link>
                                </Button>
                              </Field.Root>
                            )}
                          </Box>
                        ))}

                        <Field.Root
                          minW={0}
                          gridColumn={{ base: 'auto', md: 'span 6' }}
                          required
                          invalid={!!(formik.touched.date && formik.errors.date)}
                        >
                          <Field.Label>Datum</Field.Label>
                          <SettingsInput
                            type="date"
                            autoComplete="off"
                            name="date"
                            disabled={isReadOnly}
                            value={
                              formik.values.date
                                ? dayjs(formik.values.date).format('YYYY-MM-DD')
                                : ''
                            }
                            onChange={e => {
                              const dateValue = e.target.value
                                ? dayjs(e.target.value, 'YYYY-MM-DD').toDate()
                                : new Date()

                              // if (dateValue && dayjs(dateValue).isAfter(dayjs(), 'day')) {
                              //   toast.warning(
                              //     'Ungültiges Datum',
                              //     'Bitte wählen Sie ein Datum in der Vergangenheit oder heute.'
                              //   )
                              //   // keep current value, do not update form
                              //   return
                              // }

                              formik.setFieldValue('date', dateValue, true)
                              handleDraftChange('date', dateValue)
                              formik.setStatus(null)
                            }}
                            onBlur={() => formik.setFieldTouched('date', true, true)}
                          />

                          {formik.touched.date && formik.errors.date && (
                            <Field.ErrorText>{String(formik.errors.date)}</Field.ErrorText>
                          )}
                        </Field.Root>

                        <Field.Root
                          minW={0}
                          gridColumn={{ base: 'auto', md: 'span 3' }}
                          required={isUnionSelected}
                          invalid={
                            !!(formik.touched.activityStartTime && formik.errors.activityStartTime)
                          }
                        >
                          <Field.Label>
                            Uhrzeit von
                            {isUnionSelected ? (
                              <Field.RequiredIndicator />
                            ) : (
                              <OptionalFieldBadge />
                            )}
                          </Field.Label>
                          <SettingsInput
                            type="time"
                            disabled={isReadOnly}
                            value={(formik.values as any).activityStartTime ?? ''}
                            onChange={e => {
                              startTimeTouchedByUserRef.current = true
                              formik.setFieldValue('activityStartTime', e.target.value, true)
                              handleDraftChange('activityStartTime' as any, e.target.value)
                            }}
                            onBlur={() => formik.setFieldTouched('activityStartTime', true, true)}
                          />
                          {formik.touched.activityStartTime && formik.errors.activityStartTime && (
                            <Field.ErrorText>
                              {String(formik.errors.activityStartTime)}
                            </Field.ErrorText>
                          )}
                          {!isUnionSelected && (
                            <Flex mt={2}>
                              <Button
                                variant="outline"
                                onClick={handleClearTimes}
                                disabled={
                                  isReadOnly ||
                                  (!formik.values.activityStartTime &&
                                    !formik.values.activityEndTime)
                                }
                              >
                                <LuTimerOff />
                                Uhrzeit entfernen
                              </Button>
                            </Flex>
                          )}
                        </Field.Root>

                        <Field.Root
                          minW={0}
                          gridColumn={{ base: 'auto', md: 'span 3' }}
                          required={isUnionSelected}
                          invalid={
                            !!(formik.touched.activityEndTime && formik.errors.activityEndTime)
                          }
                        >
                          <Field.Label>
                            Bis
                            {isUnionSelected ? (
                              <Field.RequiredIndicator />
                            ) : (
                              <OptionalFieldBadge />
                            )}
                          </Field.Label>
                          <SettingsInput
                            type="time"
                            disabled={isReadOnly}
                            value={(formik.values as any).activityEndTime ?? ''}
                            onChange={e => {
                              endTimeTouchedByUserRef.current = true
                              formik.setFieldValue('activityEndTime', e.target.value, true)
                              handleDraftChange('activityEndTime' as any, e.target.value)
                            }}
                            onBlur={() => formik.setFieldTouched('activityEndTime', true, true)}
                          />
                          {formik.touched.activityEndTime && formik.errors.activityEndTime && (
                            <Field.ErrorText>
                              {String(formik.errors.activityEndTime)}
                            </Field.ErrorText>
                          )}
                        </Field.Root>

                        {isUnionSelected && (
                          <Box gridColumn={{ base: 'auto', md: '1 / -1' }}>
                            <Flex align="center" gap={3}>
                              <Checkbox.Root
                                checked={Boolean((formik.values as any).shortNoticeCancelled)}
                                disabled={isReadOnly}
                                onCheckedChange={({ checked }) => {
                                  const value = Boolean(checked)
                                  formik.setFieldValue('shortNoticeCancelled', value, true)
                                  handleDraftChange('shortNoticeCancelled' as any, value)
                                }}
                              >
                                <Checkbox.HiddenInput />
                                <Checkbox.Control />
                                <Checkbox.Label>
                                  Kurzfristig storniert durch Leistungsempfänger
                                </Checkbox.Label>
                              </Checkbox.Root>
                            </Flex>
                          </Box>
                        )}

                        {/* Ort / Stadt */}
                        <Field.Root
                          gridColumn={{ base: 'auto', md: '1 / -1' }}
                          invalid={
                            !!(formik.touched.location?.city && formik.errors.location?.city)
                          }
                        >
                          <Field.Label>
                            Ort der Leistung <Field.RequiredIndicator />
                          </Field.Label>

                          <Flex gap={4} w="full">
                            <SegmentGroup.Root
                              value={formik.values.location?.type}
                              disabled={isReadOnly}
                              onValueChange={({ value }) => {
                                formik.setFieldValue('location.type', value, true)
                                handleDraftLocationChange('type', value as any)

                                if (value === 'online') {
                                  formik.setFieldValue('location.city', '', true)
                                  handleDraftLocationChange('city', '')
                                }
                              }}
                            >
                              <SegmentGroup.Indicator />
                              <SegmentGroup.Items
                                items={[
                                  { label: 'Eigenes Büro', value: 'online' },
                                  { label: 'Anderer Ort', value: 'onsite' },
                                ]}
                              />
                            </SegmentGroup.Root>

                            <SettingsInput
                              flex="1"
                              w="full"
                              minW={0}
                              placeholder="Ort der Leistungserbringung"
                              disabled={formik.values.location?.type === 'online' || isReadOnly}
                              value={formik.values.location.city}
                              onChange={e => {
                                const sanitized = e.target.value.replace(
                                  /[^A-Za-zÀ-ÖØ-öø-ÿĀ-žА-Яа-яÄÖÜäöüß\s.'-]/g,
                                  ''
                                )
                                formik.setFieldValue('location.city', sanitized, true)
                                handleDraftLocationChange('city', sanitized)
                              }}
                              onBlur={() => formik.setFieldTouched('location.city', true, true)}
                            />
                          </Flex>

                          {formik.touched.location?.city && formik.errors.location?.city && (
                            <Field.ErrorText>
                              {String(formik.errors.location.city)}
                            </Field.ErrorText>
                          )}
                        </Field.Root>
                        <Field.Root
                          gridColumn={{ base: 'auto', md: '1 / -1' }}
                          invalid={!!(formik.touched.notes && formik.errors.notes)}
                        >
                          <Field.Label>
                            Interne Notiz
                            <Tooltip
                              showArrow
                              content="Diese Notiz wird nur zur internen Dokumentation bereitgestellt und nicht auf der Rechnung angezeigt."
                              positioning={{ placement: 'top' }}
                            >
                              <Box color="fg.muted" as="span" cursor="help">
                                <LuInfo />
                              </Box>
                            </Tooltip>
                            <OptionalFieldBadge />
                          </Field.Label>
                          <Textarea
                            disabled={isReadOnly}
                            autoComplete="off"
                            maxLength={1000}
                            {...formik.getFieldProps('notes')}
                            onChange={e => {
                              formik.handleChange(e)
                              handleDraftChange('notes', e.target.value)
                              formik.setStatus(null)
                            }}
                          />

                          <Field.HelperText>
                            {formik.values.notes?.length || 0} von 1000 Zeichen.
                          </Field.HelperText>

                          {formik.touched.notes && formik.errors.notes && (
                            <Field.ErrorText>{String(formik.errors.notes)}</Field.ErrorText>
                          )}
                        </Field.Root>

                        <Field.Root gridColumn={{ base: 'auto', md: '1 / -1' }}>
                          <Stack gap={1.5} width="full">
                            <Field.Label>
                              Anhänge <OptionalFieldBadge />
                            </Field.Label>
                            <Card.Root
                              variant="outline"
                              cursor={isReadOnly ? 'not-allowed' : 'pointer'}
                              opacity={isReadOnly ? 0.6 : 1}
                              onClick={() => {
                                if (isReadOnly) return
                                fileInputRef.current?.click()
                              }}
                            >
                              <Card.Body textAlign="center">
                                <LuUpload />
                                <Card.Title>Legen Sie Ihre Anhänge hier ab</Card.Title>
                                <Card.Description>
                                  Höchstens {MAX_ATTACHMENT_FILES} Dateien, max.{' '}
                                  {Math.round(MAX_ATTACHMENT_FILE_SIZE / (1024 * 1024))} MB pro
                                  Datei
                                </Card.Description>
                              </Card.Body>
                            </Card.Root>

                            <Input
                              ref={fileInputRef}
                              type="file"
                              multiple
                              accept={ATTACHMENT_ACCEPT_ATTR}
                              disabled={isReadOnly}
                              display="none"
                              onChange={e => {
                                handleAddAttachments(e.target.files)
                                if (e.target) e.target.value = ''
                              }}
                            />

                            {(currentAttachments.length > 0 ||
                              (editId && existingAttachments.length > 0)) && (
                              <Stack gap={3}>
                                {editId &&
                                  existingAttachments.map(item => (
                                    <Card.Root key={item.id}>
                                      <Card.Body>
                                        <Flex align="center" justify="space-between">
                                          <Flex gap={3} align="center">
                                            <LuUpload />
                                            <Box>
                                              {item.downloadUrl ? (
                                                <Button asChild variant="plain">
                                                  <a
                                                    href={item.downloadUrl}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    download
                                                  >
                                                    {item.name}
                                                  </a>
                                                </Button>
                                              ) : (
                                                <Text>{item.name}</Text>
                                              )}

                                              <Card.Description>
                                                {formatFileSize(item.size)}
                                              </Card.Description>
                                            </Box>
                                          </Flex>
                                          <IconButton
                                            aria-label="Anhang löschen"
                                            disabled={isReadOnly}
                                            variant="ghost"
                                            onClick={() => handleDeleteExistingAttachment(item.id)}
                                          >
                                            <LuX />
                                          </IconButton>
                                        </Flex>
                                      </Card.Body>
                                    </Card.Root>
                                  ))}

                                {currentAttachments.map(item => (
                                  <Card.Root key={item.id}>
                                    <Card.Body>
                                      <Flex align="center" justify="space-between">
                                        <Flex gap={3} align="center">
                                          <LuUpload />
                                          <Box>
                                            <Text>{item.file.name}</Text>
                                            <Card.Description>
                                              {formatFileSize(item.file.size)}
                                            </Card.Description>
                                          </Box>
                                        </Flex>
                                        <IconButton
                                          aria-label="Anhang entfernen"
                                          variant="ghost"
                                          disabled={isReadOnly}
                                          onClick={() => handleRemoveAttachment(item.id)}
                                        >
                                          <LuX />
                                        </IconButton>
                                      </Flex>
                                    </Card.Body>
                                  </Card.Root>
                                ))}
                              </Stack>
                            )}
                          </Stack>
                        </Field.Root>
                      </FormLayout.Fields>
                      {/* Form status */}
                      {formik.status && (
                        <Alert.Root
                          status={formik.status.type === 'success' ? 'success' : 'error'}
                        >
                          <Alert.Indicator />
                          <Alert.Description>{formik.status.message}</Alert.Description>
                        </Alert.Root>
                      )}
                    </FormLayout.Root>
                  </form>
                </Card.Body>
              </Card.Root>
            </Page.Main>

            {/* Right: Sidebar */}
            <Page.Context>
              {isUnionSelected && (
                <WorkloadQuotaCard
                  title="Kontingente"
                  values={{
                    TOJ: workload?.TOJ,
                    CONSULTING: workload?.CONSULTING,
                    TRAINING: workload?.TRAINING,
                    PILOT: workload?.PILOT,
                  }}
                  placeholder={
                    !selectedEntityId ? (
                      <WorkloadQuotaHint>
                        Bitte wählen Sie zuerst einen Leistungsempfänger.
                      </WorkloadQuotaHint>
                    ) : workloadLoading ? (
                      <WorkloadQuotaHint>Lade Kontingente…</WorkloadQuotaHint>
                    ) : workloadError ? (
                      <Alert.Root status="error">
                        <Alert.Indicator />
                        <Alert.Description>{workloadError}</Alert.Description>
                      </Alert.Root>
                    ) : undefined
                  }
                />
              )}

              {/* Calendar placeholder card (design-like) */}
              <Mini3DayCalendar
                anchorDate={formik.values.date}
                isUnionSelected={isUnionSelected}
                unionId={unionId}
                selectedEntityId={selectedEntityId}
                getActivitiesInRangeFn={getActivitiesInRange}
                heightPx={330}
                startHour={0}
                endHour={24}
                pixelsPerHour={46}
              />
            </Page.Context>
          </Page.ContextColumns>

          <TojPlausibilityDialog
            open={tojConfirmOpen}
            confirmed={tojConfirmed}
            onConfirmedChange={setTojConfirmed}
            onCancel={() => {
              tojSkipRef.current = false
              setTojConfirmOpen(false)
              pendingSubmitRef.current = null
            }}
            onContinueSave={() => {
              submitModeRef.current = 'save'
              tojSkipRef.current = true
              setTojConfirmOpen(false)
              pendingSubmitRef.current?.()
              pendingSubmitRef.current = null
            }}
            onContinueSaveAndNew={() => {
              submitModeRef.current = 'saveAndNew'
              tojSkipRef.current = true
              setTojConfirmOpen(false)
              pendingSubmitRef.current?.()
              pendingSubmitRef.current = null
            }}
          />
          <Tooltip
            disabled={!isInvoicedActivity || isCreate}
            content={
              <Box maxW="260px">
                <Text textStyle="metadata">{INVOICED_ACTIVITY_LOCK_MESSAGE}</Text>
              </Box>
            }
          >
            <DualActionBar
              visible={true}
              onCancel={handleCancel}
              cancelDisabled={formik.isSubmitting}
              onPrimary={submitSave}
              primaryLabel={isCreate ? 'Anlegen & Speichern' : 'Speichern'}
              primaryDisabled={disableSubmit}
              primaryLoading={formik.isSubmitting}
              onSecondary={isCreate ? submitSaveAndNew : undefined}
              secondaryLabel="Anlegen & neue Aktivität"
              secondaryDisabled={disableSubmit}
              secondaryLoading={formik.isSubmitting}
            />
          </Tooltip>
        </Page.Content>
      </Page.Root>
    </SibPageShell>
  )
}
